import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Routines & Habits — two DISTINCT but linkable action objects, deliberately
 * NOT merged into one generic "tracker" and NOT the same as a Target.
 *
 *  - Habit   = a single recurring behaviour tied to a weekday schedule. Tracked
 *              by binary check-ins; its signal is a streak of scheduled days met.
 *              ("Meditate on Mon/Wed/Fri.")
 *  - Routine = an ordered set of time-blocked steps you run as a block on a
 *              given day-type. Tracked by "ran it today", not a streak.
 *              ("Morning routine: stretch, journal, plan.")
 *  - Target  = a measured OUTCOME/OUTPUT commitment with a quantity + evidence
 *              rule (lives in target.ts). Count-based "3x/week any day" goals
 *              belong there, which keeps Habit purely schedule-based.
 *
 * Misses never shame: a pending day is "due", a rest day is "resting".
 */

export type HabitStatus = 'active' | 'paused' | 'retired';

/** Weekday schedule. Index 0 = Sunday ... 6 = Saturday (matches Date.getUTCDay). */
export type WeekdayMask = readonly [boolean, boolean, boolean, boolean, boolean, boolean, boolean];

export const EVERYDAY_MASK: WeekdayMask = [true, true, true, true, true, true, true];
export const WEEKDAYS_MASK: WeekdayMask = [false, true, true, true, true, true, false];
export const WEEKENDS_MASK: WeekdayMask = [true, false, false, false, false, false, true];

export type RoutineDayType = 'everyday' | 'weekdays' | 'weekends' | 'custom';

export function maskForDayType(dayType: RoutineDayType, custom?: WeekdayMask): WeekdayMask {
  switch (dayType) {
    case 'everyday':
      return EVERYDAY_MASK;
    case 'weekdays':
      return WEEKDAYS_MASK;
    case 'weekends':
      return WEEKENDS_MASK;
    case 'custom':
      return custom ?? EVERYDAY_MASK;
  }
}

/** Whether a habit/routine is scheduled to occur on the given ISO instant. */
export function isScheduledOn(mask: WeekdayMask, iso: string): boolean {
  const day = new Date(iso).getUTCDay();
  return mask[day] === true;
}

export interface Habit extends RecordEnvelope {
  readonly kind: 'habit';
  readonly title: string;
  readonly weekdayMask: WeekdayMask;
  readonly status: HabitStatus;
  /** Linked targets/projects/skills this habit supports (ids only). */
  readonly linkedIds: readonly EntityId[];
}

export interface HabitInput {
  readonly title: string;
  readonly weekdayMask?: WeekdayMask;
  readonly privacyClass?: PrivacyClass;
}

export interface HabitCheckin extends RecordEnvelope {
  readonly kind: 'habit_checkin';
  readonly habitId: EntityId;
  /** ISO date the check-in is counted under. */
  readonly occurredOn: string;
  readonly note: string;
}

export interface HabitSummary {
  readonly habitId: EntityId;
  readonly scheduledToday: boolean;
  readonly checkedInToday: boolean;
  /** Consecutive scheduled days met, counting today only if already checked in. */
  readonly currentStreakDays: number;
  readonly lastCheckinAt: string | null;
  /** Neutral status word — never shaming. */
  readonly state: 'on_track' | 'due_today' | 'resting' | 'paused';
}

const dayKey = (iso: string): string => iso.slice(0, 10);

/**
 * Pure, mask-aware habit streak. Only scheduled days can break a streak; rest
 * days are skipped. Today counts toward the streak only once checked in, and a
 * still-pending today never breaks an existing streak.
 */
export function computeHabitStreak(
  habit: Pick<Habit, 'id' | 'weekdayMask' | 'status'>,
  checkins: readonly HabitCheckin[],
  nowIso: string,
): HabitSummary {
  const days = new Set<string>();
  let lastCheckinAt: string | null = null;
  for (const c of checkins) {
    days.add(dayKey(c.occurredOn));
    if (!lastCheckinAt || c.occurredOn > lastCheckinAt) lastCheckinAt = c.occurredOn;
  }

  const todayKey = dayKey(nowIso);
  const scheduledToday = isScheduledOn(habit.weekdayMask, nowIso);
  const checkedInToday = days.has(todayKey);

  let currentStreakDays = 0;
  const cursor = new Date(nowIso);
  for (let i = 0; i < 730; i++) {
    const iso = cursor.toISOString();
    const isToday = i === 0;
    if (isScheduledOn(habit.weekdayMask, iso)) {
      if (days.has(dayKey(iso))) {
        currentStreakDays += 1;
      } else if (isToday) {
        // Today is still pending — neither counts nor breaks the streak.
      } else {
        break;
      }
    }
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }

  let state: HabitSummary['state'];
  if (habit.status === 'paused') state = 'paused';
  else if (!scheduledToday) state = 'resting';
  else if (checkedInToday) state = 'on_track';
  else state = 'due_today';

  return {
    habitId: habit.id,
    scheduledToday,
    checkedInToday,
    currentStreakDays,
    lastCheckinAt,
    state,
  };
}

export interface RoutineStep {
  readonly id: string;
  readonly label: string;
  /** Optional time box for the step, in minutes. */
  readonly minutes: number | null;
}

export interface Routine extends RecordEnvelope {
  readonly kind: 'routine';
  readonly title: string;
  readonly dayType: RoutineDayType;
  readonly weekdayMask: WeekdayMask;
  readonly steps: readonly RoutineStep[];
  readonly status: HabitStatus;
  /** ISO time the routine was last marked as run, or null. */
  readonly lastRanAt: string | null;
}

export interface RoutineInput {
  readonly title: string;
  readonly dayType?: RoutineDayType;
  readonly weekdayMask?: WeekdayMask;
  readonly steps?: readonly { label: string; minutes?: number | null }[];
  readonly privacyClass?: PrivacyClass;
}

/** Total time box of a routine, summing steps that declare minutes. */
export function routineMinutes(routine: Pick<Routine, 'steps'>): number {
  return routine.steps.reduce((sum, s) => sum + (s.minutes ?? 0), 0);
}

/** Whether a routine is due to be run today (scheduled and not yet run today). */
export function routineDueToday(routine: Pick<Routine, 'weekdayMask' | 'lastRanAt' | 'status'>, nowIso: string): boolean {
  if (routine.status !== 'active') return false;
  if (!isScheduledOn(routine.weekdayMask, nowIso)) return false;
  if (routine.lastRanAt && dayKey(routine.lastRanAt) === dayKey(nowIso)) return false;
  return true;
}
